import { useEffect, useRef, useState } from "react";

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const defaultCenter = { lat: 23.6345, lng: -102.5528 };

const hasValidCoordinates = (lat, lng) => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    !(latitude === 0 && longitude === 0)
  );
};

const extractCoordinates = (value = "") => {
  const text = decodeURIComponent(String(value)).replace(/&amp;/g, "&");
  const atMatch = text.match(/@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/);
  if (atMatch) {
    return {
      lat: Number(atMatch[1]),
      lng: Number(atMatch[2]),
    };
  }

  const queryMatch = text.match(/[?&](?:q|query|ll)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/);
  if (queryMatch) {
    return {
      lat: Number(queryMatch[1]),
      lng: Number(queryMatch[2]),
    };
  }

  const googleDataMatch = text.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (googleDataMatch) {
    return {
      lat: Number(googleDataMatch[1]),
      lng: Number(googleDataMatch[2]),
    };
  }

  const googleDirMatch = text.match(/\/dir\/[^?]*\/(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/);
  if (googleDirMatch) {
    return {
      lat: Number(googleDirMatch[1]),
      lng: Number(googleDirMatch[2]),
    };
  }

  const plainMatch = text.match(/(-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)/);
  if (plainMatch) {
    return {
      lat: Number(plainMatch[1]),
      lng: Number(plainMatch[2]),
    };
  }

  return null;
};

const isShortGoogleMapsLink = (value = "") =>
  /maps\.app\.goo\.gl|goo\.gl\/maps/i.test(String(value));

const isGoogleMapsLink = (value = "") =>
  /google\.[a-z.]+\/maps|maps\.google\.[a-z.]+|maps\.app\.goo\.gl|goo\.gl\/maps/i.test(
    String(value)
  );

const getGoogleAddressPart = (components = [], type) =>
  components.find((component) => component.types?.includes(type))?.long_name || "";

const getGoogleAddressDetails = (result) => {
  const components = result?.address_components || [];

  return {
    calle: getGoogleAddressPart(components, "route"),
    numero: getGoogleAddressPart(components, "street_number"),
    colonia:
      getGoogleAddressPart(components, "neighborhood") ||
      getGoogleAddressPart(components, "sublocality") ||
      getGoogleAddressPart(components, "sublocality_level_1"),
    ciudad:
      getGoogleAddressPart(components, "locality") ||
      getGoogleAddressPart(components, "administrative_area_level_2"),
    codigoPostal: getGoogleAddressPart(components, "postal_code"),
    displayName: result?.formatted_address || "",
  };
};

const loadGoogleMaps = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Google Maps no esta disponible."));
      return;
    }

    if (window.google?.maps) {
      resolve(window.google.maps);
      return;
    }

    if (!googleMapsApiKey) {
      reject(new Error("Falta configurar REACT_APP_GOOGLE_MAPS_API_KEY."));
      return;
    }

    const existingScript = document.querySelector("script[data-tulocal-google-map-picker]");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google.maps), { once: true });
      existingScript.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      googleMapsApiKey
    )}`;
    script.async = true;
    script.defer = true;
    script.dataset.tulocalGoogleMapPicker = "true";
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error("No se pudo cargar Google Maps."));
    document.head.appendChild(script);
  });

const LocationPicker = ({
  address,
  lat,
  lng,
  onSelect,
  onAddressChange,
  onAddressDetails,
}) => {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapsRef = useRef(null);
  const [mapError, setMapError] = useState("");
  const [searchMessage, setSearchMessage] = useState("");
  const [searchValue, setSearchValue] = useState(address || "");
  const hasCoordinates = hasValidCoordinates(lat, lng);
  const selectedPosition = hasCoordinates
    ? { lat: Number(lat), lng: Number(lng) }
    : null;

  useEffect(() => {
    let isMounted = true;
    let clickListener = null;

    const initMap = async () => {
      try {
        const maps = await loadGoogleMaps();
        if (!isMounted || !mapNodeRef.current) return;

        const center = selectedPosition || defaultCenter;
        const map = new maps.Map(mapNodeRef.current, {
          center,
          zoom: selectedPosition ? 17 : 5,
          mapTypeControl: true,
          mapTypeControlOptions: {
            mapTypeIds: ["roadmap", "hybrid"],
            position: maps.ControlPosition.TOP_RIGHT,
          },
          mapTypeId: "hybrid",
          streetViewControl: false,
          fullscreenControl: true,
          gestureHandling: "greedy",
        });

        const marker = new maps.Marker({
          position: selectedPosition || center,
          map,
          draggable: true,
          visible: Boolean(selectedPosition),
        });

        const setSelectedPoint = (position) => {
          const next = {
            lat: Number(position.lat().toFixed(7)),
            lng: Number(position.lng().toFixed(7)),
          };
          marker.setPosition(next);
          marker.setVisible(true);
          map.panTo(next);
          selectPoint(next);
        };

        clickListener = map.addListener("click", (event) => {
          if (event.latLng) setSelectedPoint(event.latLng);
        });

        marker.addListener("dragend", (event) => {
          if (event.latLng) setSelectedPoint(event.latLng);
        });

        mapRef.current = map;
        markerRef.current = marker;
        mapsRef.current = maps;
        setMapError("");

        window.setTimeout(() => {
          maps.event.trigger(map, "resize");
          map.setCenter(center);
        }, 250);
      } catch (error) {
        if (isMounted) setMapError(error.message || "No se pudo cargar Google Maps.");
      }
    };

    initMap();

    return () => {
      isMounted = false;
      if (clickListener) clickListener.remove();
    };
  }, []);

  useEffect(() => {
    setSearchValue(address || "");
  }, [address]);

  useEffect(() => {
    if (!mapRef.current || !markerRef.current || !selectedPosition) return;
    markerRef.current.setPosition(selectedPosition);
    markerRef.current.setVisible(true);
    mapRef.current.setCenter(selectedPosition);
    mapRef.current.setZoom(Math.max(mapRef.current.getZoom(), 17));
  }, [selectedPosition?.lat, selectedPosition?.lng]);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition((position) => {
      onSelect({
        lat: Number(position.coords.latitude.toFixed(7)),
        lng: Number(position.coords.longitude.toFixed(7)),
      });
    });
  };

  const selectPoint = (next) => {
    const selected = {
      lat: Number(Number(next.lat).toFixed(7)),
      lng: Number(Number(next.lng).toFixed(7)),
    };
    onSelect(selected);

    fetch(
      `/api/reverse-geocode-osm?lat=${encodeURIComponent(
        selected.lat
      )}&lng=${encodeURIComponent(selected.lng)}`
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((details) => {
        if (details?.found && onAddressDetails) onAddressDetails(details);
      })
      .catch(() => {});
  };

  const resolveGoogleMapsLink = async (url) => {
    const response = await fetch("/api/resolve-google-maps-link", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ url }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || "No pudimos leer ese link de Google Maps.");
    }

    return data.coordinates;
  };

  const searchAddress = async () => {
    setSearchMessage("");
    const cleanAddress = String(searchValue || address || "").trim();

    if (!cleanAddress) {
      setSearchMessage("Escribe una direccion o pega un link de Google Maps primero.");
      return;
    }

    if (onAddressChange) onAddressChange(cleanAddress);

    const coordinates = extractCoordinates(cleanAddress);
    if (coordinates) {
      selectPoint(coordinates);
      setSearchMessage("Ubicacion detectada. Ajusta el marcador si hace falta.");
      return;
    }

    if (isGoogleMapsLink(cleanAddress)) {
      try {
        setSearchMessage("Leyendo el link de Google Maps...");
        const resolvedCoordinates = await resolveGoogleMapsLink(cleanAddress);
        if (resolvedCoordinates) {
          selectPoint(resolvedCoordinates);
          setSearchMessage("Ubicacion detectada desde el link. Ajusta el marcador si hace falta.");
          return;
        }
      } catch (error) {
        setSearchMessage(
          error.message ||
            "No pudimos leer ese link. Escribe la direccion o marca el punto manualmente."
        );
      }
      return;
    }

    if (!mapsRef.current) {
      setSearchMessage("Google Maps todavia esta cargando. Intenta de nuevo.");
      return;
    }

    const geocoder = new mapsRef.current.Geocoder();
    geocoder.geocode(
      {
        address: cleanAddress,
        componentRestrictions: { country: "MX" },
      },
      (results, status) => {
        if (status === "OK" && results?.[0]?.geometry?.location) {
          if (onAddressDetails) {
            onAddressDetails(getGoogleAddressDetails(results[0]));
          }
          const location = results[0].geometry.location;
          selectPoint({
            lat: location.lat(),
            lng: location.lng(),
          });
          setSearchMessage("Direccion encontrada. Ajusta el marcador al frente exacto.");
          return;
        }

        setSearchMessage("No encontramos esa direccion. Intenta agregar colonia, ciudad, pegar un link completo de Google Maps o marcar el punto manualmente.");
      }
    );
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-bold text-slate-950">
            Marca la ubicacion exacta en Google Maps *
          </p>
          <p className="text-xs leading-5 text-slate-600">
            Puedes buscar la direccion aqui en cualquier momento. Ajusta el marcador al frente principal.
          </p>
          <p className="mt-1 text-[10px] text-slate-400">
            Calle, número y colonia se completan con © OpenStreetMap contributors.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={useCurrentLocation}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-100"
          >
            Usar mi ubicacion
          </button>
        </div>
      </div>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          value={searchValue}
          onChange={(event) => {
            setSearchValue(event.target.value);
            if (onAddressChange) onAddressChange(event.target.value);
          }}
          placeholder="Busca dirección, colonia o pega un link de Google Maps"
          className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100"
        />
        <button
          type="button"
          onClick={searchAddress}
          className="rounded-lg bg-[#0056D2] px-4 py-2 text-sm font-black text-white shadow-sm transition hover:bg-blue-700"
        >
          Buscar dirección
        </button>
      </div>

      <div className="relative z-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div ref={mapNodeRef} className="h-[320px] w-full" />
        {mapError && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/95 p-5 text-center">
            <div>
              <p className="font-bold text-slate-950">Google Maps no cargo.</p>
              <p className="mt-2 text-sm leading-6 text-slate-600">{mapError}</p>
            </div>
          </div>
        )}
      </div>

      {hasCoordinates ? (
        <p className="mt-2 text-xs font-semibold text-emerald-700">
          Ubicacion marcada: {Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}
        </p>
      ) : (
        <p className="mt-2 text-xs font-semibold text-orange-700">
          Falta marcar el punto exacto para publicar.
        </p>
      )}
      {searchMessage && (
        <p className="mt-2 text-xs font-semibold text-slate-600">
          {searchMessage}
        </p>
      )}
    </div>
  );
};

export default LocationPicker;
